'use client';
// ──────────────────────────────────────────────────────
// useTwoPhaseScan — Shared two-phase scan pipeline
//
// Phase 1: fast DB-only scan (<500ms) → backend result
// Phase 2: async LLM enrichment → patch UI later
//
// State machine (for AI loader UX):
//   idle → ai_processing → revealing → done
//              ↓ (error path) → error
//
// Min-delay logic:
//   Product data and a minimum display timer (2200ms) both run
//   in parallel via Promise.all(). Result only shown after BOTH
//   complete, preventing an immediate flash-through of the loader.
//
// Features:
//   • requestId guard: stale responses are fully ignored
//   • Continuous scanning: new scan cancels in-flight loader
//   • Deterministic close: closeScan() bumps requestId
//   • Fallback: if scan_fast fails, falls back to legacy scanProduct()
//   • Reduced-motion: exposes scanPhase for downstream consumers
// ──────────────────────────────────────────────────────
import { useState, useCallback, useRef } from 'react';
import { scanProductFast, scanProductEnrich, scanProduct } from '@/lib/api';
import type { ScanResponse } from '@/lib/types';

const MIN_LOADER_MS = 2200; // minimum visible AI loader duration

export type ScanPhase = 'idle' | 'ai_processing' | 'revealing' | 'done' | 'error';

type DebugStatus = 'idle' | 'loading' | 'ok' | 'not_found' | 'error';

interface DebugInfo {
    barcode: string;
    status: DebugStatus;
    timestamp: number;
}

interface TwoPhaseScanState {
    /** Current product data (null = no product showing) */
    scanData: ScanResponse | null;
    /** Legacy loading flag (true when phase=ai_processing) */
    scanLoading: boolean;
    /** Error message to display */
    scanError: string | null;
    /** Whether the overlay/product view should be visible */
    overlayOpen: boolean;
    /** Fine-grained state for AI loader UX */
    scanPhase: ScanPhase;
    /** Trigger a two-phase scan for a barcode */
    triggerScan: (barcode: string) => void;
    /** Close overlay — deterministic, ignores stale enrich */
    closeScan: () => void;
    /** Debug info for ScanDebugPanel */
    debugInfo: DebugInfo;
}

/** Returns a promise that resolves after `ms` milliseconds */
function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function useTwoPhaseScan(): TwoPhaseScanState {
    const [scanData, setScanData] = useState<ScanResponse | null>(null);
    const [scanLoading, setScanLoading] = useState(false);
    const [scanError, setScanError] = useState<string | null>(null);
    const [overlayOpen, setOverlayOpen] = useState(false);
    const [scanPhase, setScanPhase] = useState<ScanPhase>('idle');
    const [debugInfo, setDebugInfo] = useState<DebugInfo>({
        barcode: '',
        status: 'idle',
        timestamp: 0,
    });

    // Monotonic counter — every triggerScan() and closeScan() increments it.
    // Stale async responses check this before updating state.
    const requestIdRef = useRef(0);

    const triggerScan = useCallback(async (barcode: string) => {
        // Bump request ID — any in-flight response for previous scan is now stale
        const myRequestId = ++requestIdRef.current;
        const t0 = performance.now();
        console.time(`[SCAN] FAST ${barcode}`);

        // Immediately show overlay + AI processing loader
        setScanData(null);
        setScanError(null);
        setScanLoading(true);
        setScanPhase('ai_processing');
        setOverlayOpen(true);
        setDebugInfo({ barcode, status: 'loading', timestamp: Date.now() });

        try {
            // Run backend scan and minimum display timer in parallel.
            // Result is only shown after BOTH complete.
            const [data] = await Promise.all([
                // Phase 1: Fast DB-only scan (<500ms typical) with legacy fallback
                (async () => {
                    try {
                        return await scanProductFast(barcode);
                    } catch {
                        return await scanProduct(barcode);
                    }
                })(),
                // Minimum loader duration — ensures the AI orb is never a flash
                delay(MIN_LOADER_MS),
            ]);

            // Guard: only update if this is still the latest request
            if (requestIdRef.current !== myRequestId) return;

            console.timeEnd(`[SCAN] FAST ${barcode}`);
            console.log(`[SCAN] render delta (incl min-delay): ${(performance.now() - t0).toFixed(0)}ms`);

            // Transition: ai_processing → revealing (smooth fade-in of product card)
            setScanPhase('revealing');
            setScanData(data);
            setScanLoading(false);

            setDebugInfo(prev => ({
                ...prev,
                status: data.mode === 'UNKNOWN_BARCODE' ? 'not_found' : 'ok',
                timestamp: Date.now(),
            }));

            // Short transition window, then mark done
            setTimeout(() => {
                if (requestIdRef.current !== myRequestId) return;
                setScanPhase('done');
            }, 400);

            // Phase 2: Async LLM enrichment — non-blocking, does not affect loader
            if (data.mode !== 'UNKNOWN_BARCODE' && data.mode !== 'FOUND_INACTIVE') {
                console.time(`[SCAN] ENRICH ${barcode}`);
                scanProductEnrich(barcode).then(enriched => {
                    console.timeEnd(`[SCAN] ENRICH ${barcode}`);
                    // Guard: only update if this is still the latest request
                    if (requestIdRef.current !== myRequestId) return;
                    if (enriched && enriched.cards && enriched.cards.length > 0) {
                        setScanData(enriched);
                    }
                }).catch(() => {
                    // Enrich failure is non-fatal — fast data is already showing
                });
            }
        } catch (err) {
            // Guard: only update if this is still the latest request
            if (requestIdRef.current !== myRequestId) return;

            const message = (err instanceof Error && err.message) || 'خطأ غير معروف';
            setScanLoading(false);
            setScanPhase('error');

            // Friendly Hijazi error messages
            if (message.includes('404') || message.includes('غير')) {
                setScanError('ما لقينا المنتج — جرّب مرة ثانية 🔄');
                setDebugInfo(prev => ({ ...prev, status: 'not_found', timestamp: Date.now() }));
            } else {
                setScanError(`صار خطأ — جرّب مرة ثانية\n${message}`);
                setDebugInfo(prev => ({ ...prev, status: 'error', timestamp: Date.now() }));
            }
        }
    }, []);

    const closeScan = useCallback(() => {
        // Bump requestId → any in-flight enrich is now stale
        requestIdRef.current++;
        setScanData(null);
        setScanError(null);
        setScanLoading(false);
        setScanPhase('idle');
        setOverlayOpen(false);
        setDebugInfo(prev => ({ ...prev, status: 'idle' }));
    }, []);

    return {
        scanData,
        scanLoading,
        scanError,
        overlayOpen,
        scanPhase,
        triggerScan,
        closeScan,
        debugInfo,
    };
}
